import { AfterViewInit, ChangeDetectionStrategy, Component, computed, OnInit, signal, Signal, ViewChild } from '@angular/core';
import { NotificationType } from 'angular-to-phaser';
import { Observable, map } from 'rxjs';
import { View } from 'src/app/components/base.component';
import { ShadowPopoverDirective } from 'src/app/directives/shadow-popover.directive';
import { Channel, Friendship, Message, Notification } from 'src/app/dto';
import { ApplicationService } from 'src/app/services/application.service';
import { ChatbarService } from 'src/app/services/chatbar.service';
import { InboxService } from 'src/app/services/inbox.service';
import { NotificationService } from 'src/app/services/notification.service';
import { TutorialService } from 'src/app/services/tutorial.service';

@Component({
    selector: 'app-social-pane',
    templateUrl: './social.component.html',
    styleUrls: ['./social.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SocialComponent extends View implements OnInit, AfterViewInit {

    friends: Observable<Friendship[]>;
    onlineFriendsSignal: Signal<Friendship[]>;
    offlineFriendsSignal: Signal<Friendship[]>;
    receivedFriendRequestsSignal: Signal<Friendship[]>;
    sentFriendRequests: Observable<Friendship[]>;

    unreadMessages: Observable<Message[]>;
    channelsSignal: Signal<Channel[]>;
    activeChannelsSignal: Signal<Channel[]>;

    @ViewChild('tut1') tutorial1: ShadowPopoverDirective;


    private sliderControlMediaQuery: MediaQueryList = window.matchMedia('(max-width: 766px)');
    unreadAlertsSignal: Signal<Notification[]>;
    // Set inside the hideApplicationShellElements subscribe below (an external service
    // BehaviorSubject), not a template-bound handler - needs to be a signal for OnPush to see
    // the update.
    protected hideShell = signal(false);

    constructor(private coreAppService: ApplicationService,
        private inboxService: InboxService,
        private chatService: ChatbarService,
        private notificationService: NotificationService,
        private tutorialService: TutorialService) {
        super(coreAppService.authService, coreAppService.uiStateService);

        this.channelsSignal = this.inboxService.allChannelsFlatSignal;
        this.activeChannelsSignal = this.chatService.activeChannelsSignal;

        this.friends = this.inboxService.friendsList$;
        this.onlineFriendsSignal = this.inboxService.onlineFriendsSignal;
        this.offlineFriendsSignal = this.inboxService.offlineFriendsSignal;

        this.unreadAlertsSignal = this.notificationService.unreadPrivateNotificationsSignal;
        this.sentFriendRequests = this.inboxService.friendRequests$.pipe(
            map(fs => {
                const user = this.activeUser();
                return fs.filter(frq => frq.SenderUsername.localeCompare(user.Username) === 0);
            })
        );
        // computed() (not the previous .pipe(map(...)) on friendRequests$) tracks both
        // friendRequestsSignal and activeUser as real dependencies - the old RxJS map() only read
        // activeUser() untracked, so it wouldn't have recomputed had activeUser resolved without a
        // new friendRequests$ emission alongside it.
        this.receivedFriendRequestsSignal = computed(() => {
            const user = this.activeUser();
            return this.inboxService.friendRequestsSignal().filter(frq => frq.RecipientUsername.localeCompare(user.Username) === 0);
        });

        this.subscribe(this.uiStateService.hideApplicationShellElements.asObservable(), hide => this.hideShell.set(hide));
    }

    ngOnInit(): void {
        this.sliderControlMediaQuery.addEventListener('change', this.setupGlobalHiders.bind(this)); // Attach listener function on state changes
        this.setupGlobalHiders();
    }

    ngAfterViewInit(): void {
        if (!this.tutorialService.tutorialComplete) {
            this.tutorialService.addTutorialSections([this.tutorial1]);
        }
    }

    openChat(withUsername: string) {
        this.uiStateService.hideAll();
        this.chatService.FindOrCreateAndOpenChat(withUsername);
    }

    openChannel(channel: Channel): void {
        this.uiStateService.hideAll();
        this.chatService.OpenChannel(channel);
    }

    closeChannel(channel: Channel): void {
        this.chatService.CloseChannel(channel);
    }

    acceptFriend(friendship: Friendship) {
        if (friendship.targetingNotification) {
            this.subscribe(this.notificationService.markNotificationsAsRead(friendship.targetingNotification), () => { });
        }
        this.subscriptions.push(this.inboxService.addFriend(friendship.SenderUsername).subscribe());
    }

    rejectFriend(friendship: Friendship) {
        if (friendship.targetingNotification) {
            this.subscribe(this.notificationService.markNotificationsAsRead(friendship.targetingNotification), () => { });
        }
        this.subscriptions.push(this.inboxService.removeFriend(friendship.SenderUsername).subscribe());
    }

    private setupGlobalHiders(): void {
        const element = document.getElementById('social-sidebar-container');
        if (this.sliderControlMediaQuery.matches) { // If media query matches and this is a phone-sized-window
            this.uiStateService.addGlobalAutohideCollapser({ name: element.id, element });
        } else { //this bar doesn't need to autohide on desktop
            this.uiStateService.removeGlobalAutohideCollapser(element.id);
        }
    }

    markReadTimeout: NodeJS.Timeout;
    toggleSidebar(): void {
        this.uiStateService.hideAll();
        if (this.markReadTimeout) {
            clearTimeout(this.markReadTimeout);
            this.markReadTimeout = null;
        } else {
            this.markReadTimeout = setTimeout(() => this.subscribe(
                this.notificationService.markNotificationsAsRead(...this.notificationService.privateNotificationsSignal().filter(n => n.NotificationType == NotificationType.FriendshipRequest)),
                () => { }),
                2000);
        }
    }
}
